import { Global } from '@utils/common';
import { matchesNew } from '@components/bid_request/details/utils/matchesNew';
import {
  CATEGORY_COLUMN_NAME,
  INTERNAL_PART_NUMBER_COLUMN_NAME,
  SUPPLIER_PART_NUMBER_COLUMN_NAME,
} from '@components/bid_request/details/constants';
import { CustomColumnHeader, CustomColumnItem, Lot } from 'types';

// Price-update sample template gets the two default descriptive part-number columns so the
// downloaded sample carries the same columns the event seeds. Each MUST have a distinct, stable
// id: getCustomColumnHeaderIdsOrderMapForLots keys its order map by header id, so id-less headers
// all collapse onto one `undefined` key (the last header's index) and misplace the config sample's
// id-less "Spec" Description value into the wrong column. Distinct ids keep "Spec" mapped to the
// id-less Description header (index 0) and leave the part-number columns blank in the sample.
const PART_NUMBER_SAMPLE_HEADERS = [
  { column_type: 'description', display_text: INTERNAL_PART_NUMBER_COLUMN_NAME, id: 'sample-internal-part-number' },
  { column_type: 'description', display_text: SUPPLIER_PART_NUMBER_COLUMN_NAME, id: 'sample-supplier-part-number' },
];

// Category attribute column, added to the price-update sample like the part-number headers above so
// its header matches the "Category" template column the OneSchema import adds
// (see getTemplateOverrides.categoryColumn) and a filled sample auto-maps. Distinct stable id for
// the same reason as the part-number headers.
const CATEGORY_SAMPLE_HEADER = {
  column_type: 'description', display_text: CATEGORY_COLUMN_NAME, id: 'sample-category',
};

export const getDefaultCustomColumnItem = (header: CustomColumnHeader) => ({
  column_type: header.column_type,
  custom_column_header_id: header.id,
  display_text: '',
  id: `new-cci-${Math.random().toString(36).slice(2, 5)}}`,
  is_supplier_visible: header.is_supplier_visible,
  new_header_index: header.column_order_index
});

export const getDefaultCustomColumnItems = (lot: Lot) => {
  const { custom_column_headers: customColumnHeaders = [] } = lot;

  if (!customColumnHeaders?.length) return [];

  return customColumnHeaders.map((header: CustomColumnHeader) => getDefaultCustomColumnItem(header)) as CustomColumnItem[];
};

const getSampleLots = (isPriceUpdate = false) => (
  Object.values(Global.lots.sample_lots).map(lot => ({
    ...lot,
    custom_column_headers: [
      ...Object.values(lot.custom_column_headers),
      ...(isPriceUpdate ? [...PART_NUMBER_SAMPLE_HEADERS, CATEGORY_SAMPLE_HEADER] : []),
    ],
    line_items: Object.values(lot.line_items).map(lineItem => (
      {
        ...lineItem,
        custom_column_items: Object.values(lineItem.custom_column_items)
      }
    ))
  })) as Lot[]
);

const getSampleLineItems = () => {
  const sampleLots = getSampleLots();
  const firstSampleLot = sampleLots[0];
  const { line_items: lineItems } = firstSampleLot;

  return lineItems;
};

/**
 * Get sample export lots data
 * @param lots If lots is empty, return sample lots
 */
export const getSampleExportLotsData = (lots: Lot[] | undefined, isPriceUpdate = false): Lot[] => {
  // intial TLC event state without any line items
  if (!lots || lots.length === 0 || (lots.length === 1 && lots[0].name === '' && matchesNew(lots[0].id))) {
    return getSampleLots(isPriceUpdate);
  }

  return lots.map((lot: Lot, index: number) => {
    const existingLot = { ...lot };

    // When new lot is created and not yet saved, it will have empty name
    if (!existingLot.name) {
      existingLot.name = `Example Lot ${index + 1}`;
    }

    const sampleLineItems = getSampleLineItems();

    existingLot.line_items = sampleLineItems.map(lineItem => {
      const sampleLineItem = { ...lineItem };

      sampleLineItem.custom_column_items = getDefaultCustomColumnItems(lot);
      return sampleLineItem;
    });

    return existingLot;
  });
};
